class CoreSeriesMenu {
  constructor(root) {
    if (root.dataset.coreSeriesInitialized) return;

    root.dataset.coreSeriesInitialized = 'true';
    this.root = root;
    this.toggle = root.querySelector('[data-series-toggle]');
    this.panel = root.querySelector('[data-series-panel]');
    this.isOpen = false;
    this.isTouchLayout = window.matchMedia('(hover: none), (pointer: coarse)').matches;
    this.openTimer = null;
    this.openingTimer = null;

    if (!this.toggle || !this.panel) return;

    this.toggle.addEventListener('click', () => {
      if (this.openTimer) {
        clearTimeout(this.openTimer);
        this.openTimer = null;
        this.setOpen(true);
        return;
      }

      if (this.isOpening) {
        clearTimeout(this.openingTimer);
        this.openingTimer = null;
        this.isOpening = false;
        this.setOpen(true);
        return;
      }

      this.setOpen(!this.isOpen);
    });

    if (!this.isTouchLayout) {
      this.root.addEventListener('mouseenter', () => this.scheduleOpen());
      this.root.addEventListener('mouseleave', () => {
        clearTimeout(this.openTimer);
        this.openTimer = null;
      });
      this.root.addEventListener('focusin', () => this.setOpen(true));
    }

    this.panel.classList.remove('is-open');
  }

  setOpen(isOpen) {
    if (!this.toggle || !this.panel || this.isOpen === isOpen) return;

    this.isOpen = isOpen;
    this.toggle.setAttribute('aria-expanded', String(isOpen));
    this.panel.classList.toggle('is-open', isOpen);
  }

  scheduleOpen() {
    clearTimeout(this.openTimer);
    if (this.isOpen) return;

    this.openTimer = setTimeout(() => {
      this.setOpen(true);
      this.openTimer = null;
      this.isOpening = true;
      this.openingTimer = setTimeout(() => {
        this.isOpening = false;
        this.openingTimer = null;
      }, 480);
    }, 300);
  }
}

function initializeSeriesMenus(scope = document) {
  if (scope.matches?.('[data-series-menu]')) {
    scope.coreSeriesMenu = new CoreSeriesMenu(scope);
  }

  scope.querySelectorAll?.('[data-series-menu]').forEach((menu) => {
    menu.coreSeriesMenu = new CoreSeriesMenu(menu);
  });
}

function initializeDesktopSeriesSlots(scope = document) {
  scope.querySelectorAll?.('[data-desktop-series-menu] .dropdown--products').forEach((productsMenu) => {
    if (productsMenu.dataset.seriesSlotsInitialized) return;

    const toggles = Array.from(productsMenu.querySelectorAll('[data-series-slot-toggle]'));
    if (!toggles.length) return;

    productsMenu.dataset.seriesSlotsInitialized = 'true';
    const container = productsMenu.querySelector(':scope > .dropdown__container');
    const productsNav = productsMenu.querySelector('.products-dropdown__nav');
    const slotPanels = Array.from(productsMenu.querySelectorAll('[data-series-slot-panel]'));
    const gsap = window.gsap;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = reduceMotion ? 0 : 0.35;
    const slotMinWidth = productsNav
      ? parseFloat(getComputedStyle(productsNav).getPropertyValue('--series-slot-min-width')) || 60
      : 60;
    const slots = Array.from(productsMenu.querySelectorAll('[data-series-slot]'));
    let baseContainerHeight = 0;
    let baseSlotHeight = 0;
    let activeToggle = null;
    let activePanel = null;
    let activeSlot = null;
    let openTimer = null;
    let closeTimer = null;
    let activeTimeline = null;

    const rememberBaseSize = () => {
      if (!baseContainerHeight && container) baseContainerHeight = container.getBoundingClientRect().height;
      if (!baseSlotHeight) {
        const slot = productsMenu.querySelector('[data-series-slot]');
        if (slot) baseSlotHeight = slot.getBoundingClientRect().height;
      }
    };

    const clearOpenTimer = () => {
      clearTimeout(openTimer);
      openTimer = null;
    };

    const resetAll = () => {
      activeTimeline?.kill();
      activeTimeline = null;
      clearOpenTimer();
      clearTimeout(closeTimer);
      closeTimer = null;
      toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
      slotPanels.forEach((panel) => {
        panel.setAttribute('hidden', '');
        panel.style.removeProperty('margin-top');
        panel.style.removeProperty('opacity');
        panel.style.removeProperty('visibility');
        panel.style.removeProperty('transform');
      });
      productsMenu.querySelectorAll('[data-series-slot]').forEach((slot) => {
        slot.classList.remove('is-active');
        slot.style.removeProperty('width');
        slot.style.removeProperty('height');
      });
      slots.forEach((_, index) => {
        productsNav?.style.removeProperty(`--series-slot-width-${index + 1}`);
      });
      productsMenu.classList.remove('is-series-open');
      container?.style.removeProperty('height');
      activeToggle = null;
      activePanel = null;
      activeSlot = null;
    };

    const closeActive = (onComplete, immediate = false) => {
      clearOpenTimer();
      if (!activePanel || !activeSlot || !gsap || immediate) {
        resetAll();
        onComplete?.();
        return;
      }

      const closingPanel = activePanel;
      const closingSlot = activeSlot;
      const closingSlotIndex = slots.indexOf(closingSlot) + 1;
      const timeline = gsap.timeline({
        defaults: { duration, ease: 'power2.inOut', overwrite: 'auto' },
        onComplete: () => {
          if (activeTimeline !== timeline) return;
          resetAll();
          onComplete?.();
        }
      });
      activeTimeline?.kill();
      activeTimeline = timeline;
      timeline.to(closingPanel, { autoAlpha: 0, duration: duration * 0.55 }, 0);
      timeline.to(closingSlot, { height: baseSlotHeight }, duration * 0.55);
      timeline.to(productsNav, { [`--series-slot-width-${closingSlotIndex}`]: `${slotMinWidth}px` }, duration * 0.55);
      if (container) timeline.to(container, { height: baseContainerHeight }, duration * 0.55);
    };

    const openNow = (toggle) => {
      clearOpenTimer();
      rememberBaseSize();
      const category = toggle.closest('[data-products-category]');
      const slot = category?.nextElementSibling;
      const targetId = toggle.dataset.seriesSlotTarget;
      const panel = targetId ? document.getElementById(targetId) : null;
      if (!slot?.matches('[data-series-slot]') || !panel || !slot.contains(panel)) return;
      const slotIndex = slots.indexOf(slot) + 1;
      const slotWidthProperty = `--series-slot-width-${slotIndex}`;

      activeToggle = toggle;
      activePanel = panel;
      activeSlot = slot;
      toggle.setAttribute('aria-expanded', 'true');
      slot.classList.add('is-active');
      productsNav?.style.setProperty(slotWidthProperty, `${slotMinWidth}px`);
      slot.style.height = `${baseSlotHeight}px`;
      productsMenu.classList.add('is-series-open');
      panel.removeAttribute('hidden');
      gsap?.set(panel, { autoAlpha: 0, x: 0 });

      requestAnimationFrame(() => {
        const slotRect = slot.getBoundingClientRect();
        const toggleRect = toggle.getBoundingClientRect();
        const topOffset = Math.max(0, toggleRect.top - slotRect.top);
        panel.style.marginTop = `${topOffset}px`;

        const slotContentStyle = getComputedStyle(slot.querySelector('.products-dropdown__slot-content'));
        const slotPadding = parseFloat(slotContentStyle.paddingInlineStart) + parseFloat(slotContentStyle.paddingInlineEnd);
        const slotWidthCap = panel.classList.contains('products-series-slot__panel--two') ? 460 : 280;
        const targetSlotWidth = Math.max(slotMinWidth, Math.min(slotWidthCap, panel.scrollWidth + slotPadding));
        const targetSlotHeight = topOffset + panel.scrollHeight;
        const requiredHeight = topOffset + panel.scrollHeight + 32;
        const targetHeight = Math.max(baseContainerHeight, requiredHeight);

        if (!gsap) {
          productsNav?.style.setProperty(slotWidthProperty, `${targetSlotWidth}px`);
          slot.style.height = `${targetSlotHeight}px`;
          if (container) container.style.height = `${targetHeight}px`;
          panel.style.opacity = '1';
          panel.style.visibility = 'visible';
          return;
        }

        activeTimeline?.kill();
        gsap.set(panel, { autoAlpha: 0, x: 0 });
        activeTimeline = gsap.timeline({
          defaults: { duration, ease: 'power2.out', overwrite: 'auto' },
          onComplete: () => container?.style.removeProperty('height')
        });
        activeTimeline.to(productsNav, { [slotWidthProperty]: `${targetSlotWidth}px` }, 0);
        activeTimeline.to(slot, { height: targetSlotHeight }, 0);
        if (container) activeTimeline.to(container, { height: targetHeight }, 0);
        activeTimeline.to(panel, { autoAlpha: 1, duration: duration * 0.7 }, 0);
        activeTimeline.eventCallback('onComplete', () => {
          activeTimeline = null;
          container?.style.removeProperty('height');
        });
      });
    };

    toggles.forEach((toggle) => {
      toggle.addEventListener('mouseenter', () => {
        clearTimeout(closeTimer);
        closeTimer = null;
        clearOpenTimer();
        if (activeToggle === toggle) return;
        openTimer = setTimeout(() => {
          if (!toggle.matches(':hover')) return;
          if (activeToggle && activeToggle !== toggle) {
            closeActive(() => openNow(toggle));
          } else {
            openNow(toggle);
          }
        }, 200);
      });

      toggle.addEventListener('mouseleave', clearOpenTimer);
      toggle.addEventListener('click', () => {
        clearOpenTimer();
        activeToggle === toggle ? closeActive() : openNow(toggle);
      });
      toggle.addEventListener('focus', () => openNow(toggle));
    });

    productsMenu.querySelectorAll('[data-products-category], [data-series-slot]').forEach((region) => {
      region.addEventListener('mouseenter', () => {
        clearTimeout(closeTimer);
        closeTimer = null;
      });
      region.addEventListener('mouseleave', () => {
        closeTimer = setTimeout(() => {
          const activeCategory = activeToggle?.closest('[data-products-category]');
          if (!activeCategory?.matches(':hover') && !activeSlot?.matches(':hover')) closeActive();
        }, 220);
      });
    });

    const parentMenu = productsMenu.closest('details[level="top"]');
    if (parentMenu) {
      new MutationObserver(() => {
        if (!parentMenu.hasAttribute('open')) resetAll();
      }).observe(parentMenu, { attributes: true, attributeFilter: ['open'] });
    }
  });
}

initializeSeriesMenus();
initializeDesktopSeriesSlots();

new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (!(node instanceof Element)) return;
      initializeSeriesMenus(node);
      initializeDesktopSeriesSlots(node);
    });
  });
}).observe(document.documentElement, { childList: true, subtree: true });

document.addEventListener('shopify:section:load', (event) => {
  initializeSeriesMenus(event.target);
  initializeDesktopSeriesSlots(event.target);
});
